import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3" }
const _hoisted_2 = { class: "flex flex-wrap items-center gap-3" }
const _hoisted_3 = { class: "text-xs text-muted" }
const _hoisted_4 = { class: "ml-auto flex gap-2" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = ["aria-valuenow"]
const _hoisted_7 = {
  key: 1,
  class: "text-sm text-red-500"
}
const _hoisted_8 = ["src"]
const _hoisted_9 = {
  key: 3,
  class: "overflow-hidden",
  style: {"height":"0"},
  "aria-hidden": "true"
}
const _hoisted_10 = {
  class: "question-window",
  style: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '900px',
            background: '#ffffff',
            borderRadius: '24px',
            boxShadow: 'rgb(4, 4, 4) 0px 0px 0px 1px, rgba(255, 255, 255, 0.18) 0px 1px 0px inset, rgba(0, 0, 0, 0.6) 0px 0px 18px 1px',
            overflow: 'hidden',
          }
}
const _hoisted_11 = { style: {
              padding: '48px',
              display: 'flex',
              flexDirection: 'column',
              fontWeight: 600,
              fontFamily: 'JetBrains Mono, Monaco, monospace',
              fontSize: '32px',
              lineHeight: '42px',
              color: '#d0d0d0',
              background: '#292d3e',
            } }
const _hoisted_12 = ["data-question-text"]
const _hoisted_13 = {
  class: "answer-container",
  style: {
            position: 'absolute',
            top: '150px',
            left: '150px',
            right: '60px',
            margin: '0 auto',
            display: 'flex',
            height: '75%',
            flexDirection: 'column',
            justifyContent: 'center',
          }
}
const _hoisted_14 = {
  class: "cta-container",
  style: {
            position: 'absolute',
            bottom: '200px',
            left: '80px',
            right: '80px',
            textAlign: 'center',
          }
}

import { gsap } from 'gsap'
import { calculateVideoDuration } from '#shared/cms/video-animation-config'
import { createVideo } from '#shared/cms/video-recorder'
import {
  createPreviewAnimation,
  stopAnimations,
  type GSAPTimeline,
} from '#shared/cms/video-gsap-animations'
import { getBackgroundStyle, getForegroundStyle } from '#shared/cms/backgrounds'

interface PendingVideo {
  blob: Blob
  width: number
  height: number
  durationSeconds: number
}

interface AnswerLine {
  text: string
  isAfterLineBreak: boolean
  isNewParagraph: boolean
}

interface QuestionRender {
  lines: Array<{ chars: Array<{ char: string, globalIndex: number }> }>
}

export default /*@__PURE__*/_defineComponent({
  __name: 'AmaVideoGenerator',
  props: {
    question: {},
    answer: {},
    backgroundStyleId: {}
  },
  emits: ["generated", "cleared"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const backgroundStyle = computed(() => getBackgroundStyle(props.backgroundStyleId))
const foregroundStyle = computed(() => getForegroundStyle(backgroundStyle.value, 'lg'))

const generating = ref(false)
const progress = ref(0)
const error = ref<string | null>(null)
const localVideoUrl = ref<string | null>(null)

const videoContainerRef = ref<HTMLDivElement | null>(null)
const gsapAnimationRef = ref<GSAPTimeline | null>(null)

onBeforeUnmount(() => {
  if (gsapAnimationRef.value) gsapAnimationRef.value.destroy()
  if (videoContainerRef.value) stopAnimations(videoContainerRef.value)
  if (localVideoUrl.value) URL.revokeObjectURL(localVideoUrl.value)
})

const formattedQuestion = computed(() => props.question.trim())
const formattedAnswer = computed(() => props.answer.trim())
const hasContent = computed(() => Boolean(formattedQuestion.value && formattedAnswer.value))

// Deterministic per-character typing delays seeded by the question text
// so the same question always types at the same speed. ~50ms baseline
// with brief gaps between words.
const typingIntervals = computed(() => {
  if (!formattedQuestion.value) return [] as number[]
  const seed = formattedQuestion.value.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % 1000
  let randomSeed = seed
  const next = () => {
    randomSeed = (randomSeed * 9301 + 49297) % 233280
    return randomSeed / 233280
  }
  const intervals: number[] = []
  const words = formattedQuestion.value.split(' ')
  for (let w = 0; w < words.length; w++) {
    const word = words[w]!
    if (w > 0) intervals.push(Math.floor(next() * 100) + 50)
    for (let c = 0; c < word.length; c++) intervals.push(Math.floor(next() * 50) + 30)
    if (w < words.length - 1) intervals.push(Math.floor(next() * 300) + 100)
  }
  return intervals
})

const answerLines = computed<AnswerLine[]>(() => {
  if (!formattedAnswer.value) return []
  const paragraphs = formattedAnswer.value.split('\n').map(p => p.trim()).filter(Boolean)
  const lines: AnswerLine[] = []

  for (let pi = 0; pi < paragraphs.length; pi++) {
    const paragraph = paragraphs[pi]!
    const isAfterLineBreak = pi > 0
    let isFirstLineOfParagraph = true
    const sentences = paragraph.match(/[^.!?]*[.!?]+|[^.!?]+$/g) || [paragraph]

    for (const sentence of sentences) {
      const trimmed = sentence.trim()
      if (!trimmed) continue
      if (trimmed.length > 30) {
        const words = trimmed.split(' ')
        let currentLine = ''
        for (const word of words) {
          if (currentLine.length + word.length + 1 <= 30) {
            currentLine += (currentLine ? ' ' : '') + word
          }
          else {
            if (currentLine) {
              lines.push({
                text: currentLine,
                isAfterLineBreak: isAfterLineBreak && isFirstLineOfParagraph,
                isNewParagraph: pi > 0 && isFirstLineOfParagraph,
              })
              isFirstLineOfParagraph = false
            }
            currentLine = word
          }
        }
        if (currentLine) {
          lines.push({
            text: currentLine,
            isAfterLineBreak: isAfterLineBreak && isFirstLineOfParagraph,
            isNewParagraph: pi > 0 && isFirstLineOfParagraph,
          })
          isFirstLineOfParagraph = false
        }
      }
      else {
        lines.push({
          text: trimmed,
          isAfterLineBreak: isAfterLineBreak && isFirstLineOfParagraph,
          isNewParagraph: pi > 0 && isFirstLineOfParagraph,
        })
        isFirstLineOfParagraph = false
      }
    }
  }
  return lines
})

const videoDuration = computed(() => calculateVideoDuration(1, answerLines.value.length, formattedQuestion.value, typingIntervals.value))

// Word-wrapped question lines + per-character spans for the typewriter
// effect. The 39-char wrap is tuned against the 900px-wide question
// window.
const questionRender = computed<QuestionRender>(() => {
  const text = formattedQuestion.value
  if (!text) return { lines: [] }
  const maxLineLength = 39
  const words = text.split(/\s+/).filter(Boolean)
  const rawLines: string[] = []
  let currentLine = ''
  for (const word of words) {
    if (currentLine.length + word.length + 1 > maxLineLength && currentLine.length > 0) {
      rawLines.push(currentLine.trim())
      currentLine = word
    }
    else {
      currentLine += (currentLine ? ' ' : '') + word
    }
  }
  if (currentLine) rawLines.push(currentLine.trim())

  let globalIndex = 0
  return {
    lines: rawLines.map(line => ({
      chars: line.split('').map(char => ({
        char: char === ' ' ? '\u00A0' : char,
        globalIndex: globalIndex++,
      })),
    })),
  }
})

// Restart the looping GSAP preview when content or background changes,
// so the editor stays in sync with what the recorded video looks like.
async function refreshPreview () {
  if (!videoContainerRef.value) return
  if (gsapAnimationRef.value) {
    gsapAnimationRef.value.destroy()
    gsapAnimationRef.value = null
  }
  stopAnimations(videoContainerRef.value)

  if (!hasContent.value || generating.value) return

  await nextTick()
  try {
    gsapAnimationRef.value = createPreviewAnimation(videoContainerRef.value, typingIntervals.value)
  }
  catch (err) {
    console.warn('[ama-video] preview animation failed:', err)
  }
}

watch([formattedQuestion, formattedAnswer, () => props.backgroundStyleId], () => refreshPreview())
onMounted(refreshPreview)

async function generate () {
  if (!hasContent.value || !videoContainerRef.value || generating.value) return

  generating.value = true
  progress.value = 0
  error.value = null

  // Tear down the looping preview before frame capture.
  if (gsapAnimationRef.value) {
    gsapAnimationRef.value.destroy()
    gsapAnimationRef.value = null
  }
  stopAnimations(videoContainerRef.value)
  await new Promise(r => setTimeout(r, 100))

  try {
    const questionLines = [formattedQuestion.value]
    const videoAnswerLines = answerLines.value.map(l => l.text)

    const videoBlob = await createVideo({
      element: videoContainerRef.value,
      question: formattedQuestion.value,
      questionLines,
      answer: formattedAnswer.value,
      answerLines: videoAnswerLines,
      relativeDate: '',
      typingIntervals: typingIntervals.value,
      onProgress: p => { progress.value = Math.round(p * 100) },
    })

    if (localVideoUrl.value) URL.revokeObjectURL(localVideoUrl.value)
    localVideoUrl.value = URL.createObjectURL(videoBlob)

    emit('generated', {
      blob: videoBlob,
      width: 1080,
      height: 1920,
      durationSeconds: videoDuration.value,
    })
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
  finally {
    generating.value = false
    progress.value = 0
    // Resume the looping preview now the capture is done.
    refreshPreview()
  }
}

function clear () {
  if (localVideoUrl.value) URL.revokeObjectURL(localVideoUrl.value)
  localVideoUrl.value = null
  emit('cleared')
}

// gsap.ticker is used by createVideo to advance frames; making sure it
// runs on mount avoids a first-frame lag on the initial recording.
onMounted(() => {
  gsap.ticker.tick()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _cache[0] || (_cache[0] = _createElementVNode("h3", { class: "text-sm text-muted" }, " Generated video ", -1)),
      _createElementVNode("span", _hoisted_3, _toDisplayString(Math.ceil(_unref(videoDuration))) + "s · 1080×1920 ", 1),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("button", {
          type: "button",
          disabled: !_unref(hasContent) || _unref(generating),
          class: "bg-primary text-background px-3 py-1 text-sm hover:bg-primary/90 transition-colors disabled:opacity-60",
          onClick: generate
        }, _toDisplayString(_unref(generating) ? `Generating ${_unref(progress)}%` : (_unref(localVideoUrl) ? 'Regenerate' : 'Generate')), 9, _hoisted_5),
        (_unref(localVideoUrl))
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "text-sm text-muted hover:text-red-500",
              onClick: clear
            }, " Remove "))
          : _createCommentVNode("", true)
      ])
    ]),
    (_unref(generating))
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "w-full h-1 bg-accent overflow-hidden",
          role: "progressbar",
          "aria-valuenow": _unref(progress),
          "aria-valuemin": "0",
          "aria-valuemax": "100"
        }, [
          _createElementVNode("div", {
            class: "h-full bg-primary transition-all duration-200",
            style: _normalizeStyle({ width: `${_unref(progress)}%` })
          }, null, 4)
        ], 8, _hoisted_6))
      : _createCommentVNode("", true),
    (_unref(error))
      ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_unref(error)), 1))
      : _createCommentVNode("", true),
    (_unref(localVideoUrl))
      ? (_openBlock(), _createElementBlock("video", {
          key: 2,
          src: _unref(localVideoUrl),
          controls: "",
          class: "block bg-accent max-w-xs aspect-[9/16] w-full"
        }, null, 8, _hoisted_8))
      : _createCommentVNode("", true),
    (_unref(hasContent))
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", {
            ref_key: "videoContainerRef",
            ref: videoContainerRef,
            class: "video-container",
            style: {
          width: '1080px',
          height: '1920px',
          position: 'relative',
          overflow: 'hidden',
          display: 'block',
          isolation: 'isolate',
        }
          }, [
            _createElementVNode("div", {
              style: _normalizeStyle({ position: 'absolute', inset: '-9em', ..._unref(backgroundStyle).style })
            }, null, 4),
            _createElementVNode("div", _hoisted_10, [
              _cache[1] || (_cache[1] = _createStaticVNode("<div style=\"height:64px;background:#292d3e;border-top-left-radius:24px;border-top-right-radius:24px;display:flex;align-items:center;padding-left:32px;position:relative;\"><div style=\"display:flex;gap:16px;\"><div style=\"width:24px;height:24px;border-radius:50%;background:#ff5f57;border:1px solid #e53e3e;\"></div><div style=\"width:24px;height:24px;border-radius:50%;background:#ffbd2e;border:1px solid #d69e2e;\"></div><div style=\"width:24px;height:24px;border-radius:50%;background:#28ca42;border:1px solid #38a169;\"></div></div><div style=\"position:absolute;left:50%;transform:translateX(-50%);color:#64748b;font-size:32px;white-space:nowrap;font-weight:500;font-family:-apple-system, BlinkMacSystemFont, &#39;Segoe UI&#39;, Roboto, sans-serif;\"> ~/question.md </div></div>", 1)),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("div", {
                  class: "question-content",
                  "data-question-text": _unref(formattedQuestion),
                  style: {
                flex: 1,
                position: 'relative',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                lineHeight: '42px',
                paddingRight: '40px',
                overflowWrap: 'break-word',
              }
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(questionRender).lines, (line, lineIndex) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: lineIndex,
                      style: {"display":"block"}
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(line.chars, (chunk) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: chunk.globalIndex,
                          class: _normalizeClass(`question-char char-${chunk.globalIndex}`),
                          style: { opacity: 0, visibility: 'hidden' }
                        }, _toDisplayString(chunk.char), 3))
                      }), 128))
                    ]))
                  }), 128))
                ], 8, _hoisted_12)
              ])
            ]),
            _createElementVNode("div", _hoisted_13, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(answerLines), (line, lineIndex) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: lineIndex,
                  class: "answer-line",
                  style: _normalizeStyle({
              color: '#000000',
              fontFamily: '-apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif',
              fontSize: '58px',
              fontWeight: '600',
              lineHeight: '1.2',
              textShadow: '0 2px 4px rgba(255, 255, 255, 0.8)',
              marginBottom: '12px',
              marginTop: line.isNewParagraph ? '48px' : (line.isAfterLineBreak ? '36px' : '0'),
            })
                }, _toDisplayString(line.text), 5))
              }), 128))
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", {
                style: _normalizeStyle({
              ..._unref(foregroundStyle),
              fontFamily: '-apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif',
              fontSize: '48px',
              fontWeight: 'bold',
              marginBottom: '24px',
            })
              }, " Ask me anything at ", 4),
              _createElementVNode("div", {
                style: _normalizeStyle({
              ..._unref(foregroundStyle),
              fontFamily: '-apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif',
              fontSize: '64px',
              fontWeight: 'bold',
              textDecoration: 'underline',
            })
              }, " roe.dev/ama ", 4)
            ])
          ], 512)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})